const GRID_INSERTABLE_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'image/avif',
]);

/**
 * Returns true if the given MIME type represents an image that can be inserted
 * into a NotePartGrid. Uses a conservative allowlist of types verified to
 * generate a preview in the notebook.
 */
export function isGridInsertableImageType(fileType: string | undefined): boolean {
  if (!fileType) {
    return false;
  }
  return GRID_INSERTABLE_IMAGE_TYPES.has(fileType);
}

export const GRID_IMAGE_ACCEPT = [...GRID_INSERTABLE_IMAGE_TYPES].join(',');

export const MAX_GRID_IMAGES = 20;

export const DEFAULT_GRID_PLACEHOLDER_WIDTH = 300;

/**
 * Orders files dropped onto a note ahead of grid insertion.
 *
 * Only two or more previewable images form a grid, so in that case the images
 * are grouped first (capped at MAX_GRID_IMAGES) ahead of the remaining files.
 * With fewer than two previewable images no grid is created, so the original
 * drop order is preserved — a single image dropped alongside other files (e.g.
 * a PDF) inserts in the exact order the user dropped them.
 *
 * `exceededMaxGridImages` reports whether the drop contained more previewable
 * images than a grid can hold, so callers can surface a warning.
 */
export function orderDroppedFilesForGrid(files: Array<File>): {
  orderedFiles: Array<File>;
  exceededMaxGridImages: boolean;
} {
  const images = files.filter((f) => isGridInsertableImageType(f.type));
  const exceededMaxGridImages = images.length > MAX_GRID_IMAGES;
  if (images.length < 2) {
    return {orderedFiles: files, exceededMaxGridImages};
  }
  const nonImages = files.filter((f) => !isGridInsertableImageType(f.type));
  return {
    orderedFiles: [...images.slice(0, MAX_GRID_IMAGES), ...nonImages],
    exceededMaxGridImages,
  };
}
